import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api, { apiErr, fmtTimeLong, fmtTime, analyzeCostSeconds, qualityEvalCostSeconds } from "../../lib/api";
import { Card, SectionTitle, Alert, btnPrimary, btnSecondary, CostBadge } from "../ui-bits";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle2, AlertTriangle, XCircle, Clock, Loader2, Gauge, Lock, Sparkles, Highlighter, PenLine, Wand2 } from "lucide-react";
import QualityPanel from "./QualityPanel";
import { useQuota } from "../../context/QuotaContext";
import { useUploads } from "../../context/UploadContext";
import OneClickModal from "../OneClickModal";

// "Producir automáticamente" REACTIVADO (Fase 6 — feb 2026). Antes estaba en pausa porque el
// B-roll automático solo tiraba de Pexels y no podía representar contenido específico real.
// Con la Fase 3-4 el backend adquirió _auto_fill_broll_v2, que iteran el `storyboard` del guion
// escena a escena y descargan cada plano de la fuente más adecuada entre 10 (wikimedia, met,
// smithsonian, wellcome, internet archive, openverse, pixabay, unsplash, pexels y fallback IA
// Gemini). run_auto_produce
// ahora invoca la v2 primero y solo cae al legacy si un segment carece de storyboard.
const AUTO_PRODUCE_ENABLED = true;

const SAMPLE = `## Escena 01: 700 metros bajo Atacama
Treinta y tres hombres quedaron sepultados a 700 metros de profundidad, y durante 17 días nadie en la superficie supo si seguían vivos. El 5 de agosto de 2010, la mina San José, cerca de Copiapó, en el desierto de Atacama, colapsó y selló la única vía de escape con un bloque de granito de 700.000 toneladas. Analicé los informes oficiales del rescate y los testimonios de los propios mineros para reconstruir lo que de verdad ocurrió ahí abajo. Los equipos de rescate calculaban en privado menos de un 2% de probabilidad de encontrar supervivientes. Pero el día 17, una sonda perforadora subió a la superficie con una nota atada a la punta, escrita con letra temblorosa: "Estamos bien en el refugio, los 33". Todo el mundo daba por hecho que una tragedia minera de esa profundidad terminaba en un funeral colectivo. Sin embargo, esos 33 hombres no solo seguían vivos: habían organizado turnos, racionado comida para dos días hasta que durara diez, y elegido un líder. Lo que la superficie tardaría 69 días en lograr, ellos ya lo habían empezado a resolver solos, en la oscuridad.

## Escena 02: El plan que nadie garantizaba
La noticia de que había supervivientes abrió una pregunta más incómoda que la primera: sacarlos vivos podía tardar meses, y nadie sabía si el túnel de rescate aguantaría sin provocar un segundo derrumbe. NASA envió ingenieros para diseñar la cápsula de rescate junto a la Armada de Chile, porque las condiciones de presión y aislamiento a esa profundidad se parecían más a las de una misión espacial que a un rescate minero convencional. Se probaron tres perforadoras distintas en simultáneo, con nombres propios: Plan A, Plan B y Plan C, cada una avanzando por una ruta distinta hacia el refugio. El Plan B, con la máquina Schramm T130, fue el primero en romper la roca final, el 9 de octubre, tras 33 días de perforación casi ininterrumpida. La cápsula terminada se llamó Fénix, tenía poco más de 50 centímetros de diámetro y debía subir a cada hombre, uno por uno, por un túnel apenas más ancho que sus hombros. Los médicos advirtieron que el ascenso de 15 minutos podía provocar pánico, náuseas o un paro cardíaco en cualquiera de ellos.

## Escena 03: Lo que subió con ellos
El 13 de octubre de 2010, a la 1:10 de la madrugada, Florencio Ávalos se convirtió en el primer minero en salir con vida dentro de la cápsula Fénix, ante 1.500 millones de personas siguiendo la transmisión en directo. Uno a uno, los 33 fueron subiendo durante las siguientes 22 horas, hasta que Luis Urzúa, el capataz que los mantuvo organizados los primeros 17 días de silencio, salió el último. Ese liderazgo bajo tierra fue, tanto como la ingeniería de superficie, lo que Chile terminó llamando después el verdadero milagro. Hoy, la mina San José permanece cerrada, pero la cápsula Fénix se exhibe como recordatorio de que la coordinación humana, cuando falla todo lo demás, también rescata vidas.`;

export default function TabGuion({ project, refreshProject, setTab, channel }) {
  const { t } = useTranslation();
  const [text, setText] = useState(project?.script || "");
  const [result, setResult] = useState(
    project?.segments?.length ? { segments: project.segments, valid: true, quality: project.quality } : null
  );
  const [busy, setBusy] = useState(false);
  const [rightTab, setRightTab] = useState("calidad");
  const [editorView, setEditorView] = useState("tecnicas"); // "editar" | "tecnicas"
  const fileRef = useRef();
  const { quota, openUpgrade } = useQuota();
  const isFree = (quota?.plan || "gratis") === "gratis" && !quota?.has_spendable_credits;
  const { startScriptAnalyze, scripts, registerReload } = useUploads();
  const evaluating = scripts.some((s) => s.id === project.id && s.kind === "analyze" && s.status === "evaluating");

  useEffect(() => registerReload && registerReload(project.id, refreshProject), [project.id, refreshProject, registerReload]);

  // El estado "mejorando/generando" se deriva del proyecto (el sondeo vive en el Builder),
  // así se mantiene aunque cambies de pestaña y vuelvas.
  const improving = project?.script_gen?.status === "generando";

  // Cuando una generación/mejora termina (generando → completo), sincroniza el editor con el resultado.
  const prevGenStatus = useRef(project?.script_gen?.status);
  useEffect(() => {
    const st = project?.script_gen?.status;
    if (st === "completo" && prevGenStatus.current === "generando") {
      setText(project.script || "");
      setResult({ segments: project.segments, valid: true, quality: project.quality });
    }
    prevGenStatus.current = st;
  }, [project?.script_gen?.status, project?.script, project?.segments, project?.quality]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error(t("guion.fileTooBig")); return; }
    const reader = new FileReader();
    reader.onload = () => { setText(reader.result); toast.success(t("guion.fileLoaded", { name: f.name })); };
    reader.readAsText(f);
  };

  // Producir automáticamente: ahora abre el MISMO modal/pipeline de "1 clic" (narración +
  // B-roll multi-fuente + pausa de aprobación + render + miniatura + publicar) en vez del
  // antiguo /auto-produce directo a la pestaña Renderizar — un solo pipeline que mantener en
  // vez de dos divergiendo con el tiempo, y el usuario ve la misma pantalla rica de progreso.
  // `autoStatus` se conserva como respaldo por si quedó una producción legacy a medias.
  const [showOneClick, setShowOneClick] = useState(false);
  const autoStatus = project?.auto_produce?.status;
  // Coste real de "Producir automáticamente": el mismo que verá dentro del modal (render +
  // posible storyboard con IA si el guion no lo trae ya), no solo la duración bruta del vídeo —
  // sin esto la tarjeta mostraba un número y el modal otro distinto al abrirse.
  const [autoProduceCost, setAutoProduceCost] = useState(0);
  useEffect(() => {
    if (!(result?.segments?.length >= 2)) return;
    let cancel = false;
    api.post("/cost/estimate", { action: "one_click_from_script", project_id: project.id })
      .then(({ data }) => { if (!cancel && data?.seconds != null) setAutoProduceCost(data.seconds); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [project.id, result?.segments?.length]);

  const analyze = async () => {
    if (!text.trim()) { toast.warning(t("guion.pasteFirst")); return; }
    setBusy(true);
    try {
      const data = await startScriptAnalyze(project.id, project.name, text);
      setResult(data);
      await refreshProject();
      if (data.segment_count < 2) toast.warning(t("guion.tooFewScenes"));
      else if (data.valid) toast.success(t("guion.analyzedOk", { count: data.segment_count }));
      else toast.warning(t("guion.analyzedWarn"));
      if (data.narrations_kept > 0) toast.info(t("guion.narrationsKept", { count: data.narrations_kept }));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const totalWords = result?.segments?.reduce((a, s) => a + s.words, 0) || 0;
  const totalDur = result?.segments?.reduce((a, s) => a + s.duration, 0) || 0;
  const estCost = (isFree || !text.trim()) ? 0 : analyzeCostSeconds(text.length) + qualityEvalCostSeconds(text.length);
  const quality = result?.quality || project?.quality;
  // Técnicas de retención subrayables. Dos fuentes: las anotaciones del propio guionista
  // (segment.tecnicas_autor, spans completos extraídos de los tags [hook]...[/hook]) y,
  // como respaldo para guiones pegados a mano, las citas verificadas del evaluador (quality.tecnicas).
  const tecnicas = quality?.tecnicas || {};
  const viewSegments = result?.segments?.length ? result.segments : (project?.segments || []);
  const hasTecnicas = viewSegments.some((s) => s.tecnicas_autor?.length > 0)
    || Object.values(tecnicas).some((arr) => arr?.length > 0);
  // El subrayado se calculó sobre los segmentos guardados: si el texto del editor cambió, avisa.
  const tecnicasStale = hasTecnicas && text.trim() !== (project?.script || "").trim();
  const qBadge = !quality ? "" : quality.score >= 85 ? "bg-emerald-100 text-emerald-700"
    : quality.score >= 70 ? "bg-blue-100 text-blue-700"
    : quality.score >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
  const sceneHasError = result?.segments?.some((s) => s.warnings?.some((w) => w.type === "error"));
  const sceneHasWarn = result?.segments?.some((s) => s.warnings?.some((w) => w.type === "warning"));
  const sceneDot = sceneHasError ? "bg-red-500" : sceneHasWarn ? "bg-amber-500" : "";

  return (
    <div className="space-y-8">
      <SectionTitle sub={t("guion.subtitle")}>{t("guion.title")}</SectionTitle>

      {improving && (
        <Alert type="info"><span className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> {t("guion.improvingInfo")}</span></Alert>
      )}

      {/* Producir automáticamente: el camino de 2 clics (guion → vídeo). El wizard queda como modo edición.
          En pausa hasta tener fuentes de B-roll fiables para contenido específico real — ver AUTO_PRODUCE_ENABLED arriba. */}
      {AUTO_PRODUCE_ENABLED && (autoStatus === "en_curso" ? (
        <Alert type="info" testid="auto-produce-running">
          <span className="inline-flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> {t("guion.autoInProgress")}</span>
            <button onClick={() => setTab("renderizar")} className="font-bold text-blue-700 underline">{t("guion.autoGoRender")}</button>
          </span>
        </Alert>
      ) : (result?.segments?.length >= 2 && !improving && (
        <Card className="p-5 border-2 border-amber-200 bg-amber-50/50" data-testid="auto-produce-card">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div>
              <h3 className="text-xl font-bold font-head text-slate-900 flex items-center gap-2">
                <Wand2 size={22} className="text-amber-600" /> {t("guion.autoTitle")}
              </h3>
              <p className="text-base text-slate-600 mt-1">{t("guion.autoDesc")}</p>
            </div>
            <button data-testid="auto-produce-button" onClick={() => setShowOneClick(true)}
              className="shrink-0 min-h-[48px] px-4 sm:px-5 text-base font-bold rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap">
              <Wand2 size={20} /> <span className="whitespace-nowrap">{t("guion.autoBtn")}</span>
              {(autoProduceCost || totalDur) > 0 && <CostBadge seconds={autoProduceCost || totalDur} tip={t("guion.autoCostTip")} />}
            </button>
          </div>
        </Card>
      )))}

      {/* Panel inline (no popup) justo debajo del botón que lo abre — mismo patrón que el
          panel "Nuevo Vídeo" manual de Mis Vídeos. */}
      {showOneClick && (
        <OneClickModal
          projectId={project.id}
          channelId={project.channel_id}
          channel={channel}
          estSeconds={totalDur}
          onClose={() => setShowOneClick(false)}
          onCreated={async () => { setShowOneClick(false); await refreshProject(); }}
        />
      )}

      {/* Divisor: dice al usuario que lo de abajo (editor de guion) es la alternativa manual al
          botón de arriba — solo tiene sentido mostrarlo si ese botón está realmente visible. */}
      {AUTO_PRODUCE_ENABLED && autoStatus !== "en_curso" && result?.segments?.length >= 2 && !improving && (
        <div className="flex items-center gap-3 text-slate-400" data-testid="manual-divider">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-sm font-semibold uppercase tracking-wide">{t("guion.manualDivider")}</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="mb-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h3 className="text-xl font-bold font-head text-slate-900">{t("guion.editor")}</h3>
              {editorView === "editar" && (
                <div className="flex gap-2">
                  <button onClick={() => setText(SAMPLE)} className="text-blue-600 font-semibold text-base hover:underline">{t("guion.useSample")}</button>
                  <input ref={fileRef} type="file" accept=".md,.txt" onChange={onFile} className="hidden" data-testid="script-file-input" />
                  <button data-testid="upload-script-button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 text-slate-700 font-semibold text-base hover:text-blue-600">
                    <Upload size={20} /> {t("guion.uploadMd")}
                  </button>
                </div>
              )}
            </div>
            {hasTecnicas && (
              <div className="flex rounded-lg border-2 border-slate-200 overflow-hidden w-fit mt-3">
                <button data-testid="editor-view-techniques" onClick={() => setEditorView("tecnicas")}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold transition-colors ${editorView === "tecnicas" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:text-slate-900"}`}>
                  <Highlighter size={15} /> {t("guion.viewTechniques")}
                </button>
                <button data-testid="editor-view-edit" onClick={() => setEditorView("editar")}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold transition-colors ${editorView === "editar" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:text-slate-900"}`}>
                  <PenLine size={15} /> {t("guion.viewEdit")}
                </button>
              </div>
            )}
          </div>
          {editorView === "tecnicas" && hasTecnicas ? (
            <TechniquesView segments={viewSegments} tecnicas={tecnicas} stale={tecnicasStale} t={t} />
          ) : (
          <div className="relative rounded-xl border border-[#e6dfc9] bg-[#fffbf0] shadow-[0_1px_2px_rgba(0,0,0,0.05),0_20px_36px_-20px_rgba(41,33,14,0.35)] overflow-hidden focus-within:border-blue-600 focus-within:ring-4 focus-within:ring-blue-600/20 transition-shadow">
            {/* Doblez de página, esquina superior derecha */}
            <div className="absolute top-0 right-0 w-7 h-7 pointer-events-none z-20"
              style={{ background: "linear-gradient(135deg, transparent 50%, #eee4c8 50%)", boxShadow: "-3px 3px 4px rgba(41,33,14,0.12)" }} />
            {/* Margen tipo cuaderno */}
            <div className="absolute left-11 top-0 bottom-0 w-px bg-red-300/60 pointer-events-none z-10" />
            <textarea
              data-testid="script-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"## Escena 01\n...\n\n## Escena 02\n..."}
              className="relative z-10 w-full h-[420px] bg-transparent text-[#332c1f] pl-14 pr-6 py-5 text-[15px] font-mono outline-none resize-none vb-scroll placeholder:text-[#a89f88] placeholder:italic"
              style={{
                lineHeight: "1.75rem",
                backgroundImage: "repeating-linear-gradient(to bottom, transparent, transparent calc(1.75rem - 1px), rgba(41,33,14,0.09) calc(1.75rem - 1px), rgba(41,33,14,0.09) 1.75rem)",
                backgroundAttachment: "local",
              }}
            />
          </div>
          )}
          <button data-testid="analyze-script-button" onClick={analyze} disabled={busy || improving} className={`w-full mt-4 ${btnPrimary}`}>
            <FileText size={22} /> {busy ? t("guion.analyzing") : t("guion.analyzeBtn")}
            {!busy && estCost > 0 && <CostBadge seconds={estCost} prefix="≤" tip={t("guion.analyzeCostTip")} />}
            {!busy && isFree && text.trim() && <CostBadge free tip={t("guion.analyzeFreeTip")} />}
          </button>
        </Card>

        <Card className="p-6">
          <div className="flex gap-1 mb-5 border-b-2 border-slate-100">
            <button data-testid="tab-calidad" onClick={() => setRightTab("calidad")}
              className={`px-4 py-2 text-base font-bold -mb-0.5 border-b-2 transition-colors inline-flex items-center gap-2 ${rightTab === "calidad" ? "text-blue-700 border-blue-600" : "text-slate-400 border-transparent hover:text-slate-600"}`}>
              <Gauge size={18} /> {t("guion.tabQuality")}
              {quality && <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${qBadge}`}>{quality.score}</span>}
              {evaluating && !quality && <Loader2 className="animate-spin text-blue-500" size={15} />}
            </button>
            <button data-testid="tab-escenas" onClick={() => setRightTab("escenas")}
              className={`px-4 py-2 text-base font-bold -mb-0.5 border-b-2 transition-colors inline-flex items-center gap-2 ${rightTab === "escenas" ? "text-blue-700 border-blue-600" : "text-slate-400 border-transparent hover:text-slate-600"}`}>
              {t("guion.tabScenes")}{result?.segments?.length ? ` (${result.segments.length})` : ""}
              {sceneDot && <span data-testid="scene-warning-dot" className={`w-2.5 h-2.5 rounded-full ${sceneDot}`} />}
            </button>
          </div>

          {rightTab === "escenas" ? (
            !result ? (
              <Alert type="info">{t("guion.noAnalysis")}</Alert>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <Stat label={t("guion.scenes")} value={result.segments.length} />
                  <Stat label={t("guion.wordsLabel")} value={totalWords} />
                  <Stat label={t("guion.durationLabel")} value={fmtTimeLong(totalDur)} />
                </div>
                {result.segments.length < 2 && <Alert type="error">{t("guion.min2scenes")}</Alert>}
                <div className="space-y-3 max-h-[360px] overflow-y-auto vb-scroll pr-1" data-testid="segments-preview">
                  {result.segments.map((s) => {
                    const hasErr = s.warnings?.some((w) => w.type === "error");
                    const hasWarn = s.warnings?.some((w) => w.type === "warning");
                    const Icon = hasErr ? XCircle : hasWarn ? AlertTriangle : CheckCircle2;
                    const color = hasErr ? "text-red-600 border-red-200 bg-red-50" : hasWarn ? "text-amber-600 border-amber-200 bg-amber-50" : "text-emerald-600 border-emerald-200 bg-emerald-50";
                    return (
                      <div key={s.id} className={`border-2 rounded-lg p-4 ${color.split(" ").slice(1).join(" ")}`}>
                        <div className="flex items-start gap-2">
                          <Icon size={22} className={color.split(" ")[0]} />
                          <div className="flex-1">
                            <div className="font-bold text-slate-900 text-base">{t("guion.sceneN", { num: s.num, title: s.title })}</div>
                            <div className="text-sm text-slate-600 mt-0.5">{t("guion.sceneMeta", { words: s.words, dur: fmtTimeLong(s.duration) })}</div>
                            {s.keywords?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`scene-keywords-${s.num}`}>
                                {s.keywords.map((k) => <span key={k} className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded">{k}</span>)}
                              </div>
                            )}
                            {s.personajes?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`scene-characters-${s.num}`}>
                                {s.personajes.map((p) => <span key={p} className="bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1">👤 {p}</span>)}
                              </div>
                            )}
                            {s.warnings?.map((w, i) => <div key={i} className="text-sm font-medium mt-1">{w.type === "error" ? "❌" : "⚠️"} {w.msg}</div>)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {result.segments.length >= 2 && (
                  <button onClick={() => setTab("narracion")} className={`w-full ${btnSecondary}`}>{t("guion.continueNarration")}</button>
                )}
              </div>
            )
          ) : (
            isFree ? (
              <div className="text-center py-6" data-testid="quality-locked">
                <div className="mx-auto w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-3"><Lock size={26} className="text-amber-600" /></div>
                <h4 className="text-lg font-bold text-slate-900 mb-1">{t("guion.lockedTitle")}</h4>
                <p className="text-base text-slate-600 mb-4">{t("guion.lockedDesc")}</p>
                <button data-testid="quality-upgrade-btn" onClick={() => openUpgrade({ error: "plan_required", msg: t("guion.lockedUpgradeMsg") })} className={`${btnPrimary} mx-auto`}><Sparkles size={20} /> {t("guion.seePlans")}</button>
                {result?.segments?.length >= 2 && (
                  <button data-testid="continue-narration-free" onClick={() => setTab("narracion")} className={`${btnSecondary} mx-auto mt-3`}>{t("guion.continueNarration")}</button>
                )}
              </div>
            ) : quality ? (
              <QualityPanel quality={quality} projectId={project.id} totalWords={totalWords} improving={improving}
                improveCount={project?.improve_count || 0}
                onImproving={() => refreshProject()} onContinue={() => setTab("narracion")} />
            ) : evaluating ? (
              <div className="flex items-center gap-3 text-slate-600 py-6" data-testid="quality-evaluating">
                <Loader2 className="animate-spin text-blue-600" size={22} />
                <span className="text-base font-semibold">{t("guion.evaluating")}</span>
              </div>
            ) : (
              <Alert type="info">{t("guion.analyzeForQuality")}</Alert>
            )
          )}
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-3 text-center">
      <div className="text-xl font-bold font-head text-slate-900">{value}</div>
      <div className="text-sm text-slate-600">{label}</div>
    </div>
  );
}

// ---- Vista "Técnicas": el guion en solo lectura con las técnicas de retención subrayadas ----
// Los datos vienen de quality.tecnicas (evaluación IA con citas literales verificadas en backend).
// Clases estáticas (no interpoladas) para que Tailwind las incluya en el build.
const MARK_STYLES = {
  hook: "bg-amber-200/80 border-b-2 border-amber-500",
  loop: "bg-sky-200/80 border-b-2 border-sky-500",
  payoff: "bg-emerald-200/80 border-b-2 border-emerald-500",
  cta: "bg-rose-200/80 border-b-2 border-rose-500",
  estilo: "bg-violet-200/80 border-b-2 border-violet-500",
};
const LEGEND_STYLES = {
  hook: "bg-amber-100 text-amber-800 border-amber-300",
  loop: "bg-sky-100 text-sky-800 border-sky-300",
  payoff: "bg-emerald-100 text-emerald-800 border-emerald-300",
  cta: "bg-rose-100 text-rose-800 border-rose-300",
  estilo: "bg-violet-100 text-violet-800 border-violet-300",
};
const GROUP_ORDER = ["hook", "loop", "payoff", "cta", "estilo"];
const segKeyOf = (num) => String(num || "").replace(/^0+/, "") || "0";

function TechniquesView({ segments, tecnicas, stale, t }) {
  // Preferimos las anotaciones del guionista (spans completos, exactos); si la escena no
  // tiene, usamos las citas verificadas del evaluador como respaldo.
  const marksFor = (s) => (s.tecnicas_autor?.length ? s.tecnicas_autor : (tecnicas[segKeyOf(s.num)] || []));
  const groupsPresent = GROUP_ORDER.filter((g) =>
    segments.some((s) => marksFor(s).some((m) => m.grupo === g)));
  return (
    <div data-testid="techniques-view">
      {stale && (
        <div data-testid="techniques-stale" className="mb-3 flex items-start gap-2 text-sm font-medium text-amber-800 bg-amber-50 border-2 border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {t("guion.techniquesStale")}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {groupsPresent.map((g) => (
          <span key={g} className={`text-xs font-bold px-2.5 py-1 rounded-full border ${LEGEND_STYLES[g]}`}>{t(`tecnicas.grupo.${g}`)}</span>
        ))}
        <span className="text-xs text-slate-400">{t("guion.techniquesHint")}</span>
      </div>
      <div className="h-[420px] overflow-y-auto vb-scroll space-y-5 rounded-xl border-2 border-slate-200 bg-white p-5">
        {segments.map((s) => {
          const k = segKeyOf(s.num);
          const stext = s.text || "";
          const marks = marksFor(s).filter((m) => m.start >= 0 && m.end <= stext.length && m.end > m.start);
          return (
            <div key={s.id || k}>
              <div className="text-sm font-bold text-slate-500 mb-1">{t("guion.sceneN", { num: s.num, title: s.title })}</div>
              <p className="text-[15px] leading-7 text-slate-800 whitespace-pre-wrap">{renderMarked(stext, marks, k, t)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function renderMarked(text, marks, segKey, t) {
  if (!marks.length) return text;
  const out = [];
  let pos = 0;
  marks.forEach((m, i) => {
    if (m.start < pos) return; // solape inesperado: se omite la marca, nunca se rompe el texto
    if (m.start > pos) out.push(text.slice(pos, m.start));
    out.push(
      <span key={`${segKey}-${i}`} className="relative inline-block group">
        <mark data-testid={`technique-mark-${segKey}-${i}`}
          className={`cursor-help rounded px-0.5 ${MARK_STYLES[m.grupo] || MARK_STYLES.estilo}`}>
          {text.slice(m.start, m.end)}
        </mark>
        <span data-testid={`technique-popup-${segKey}-${i}`}
          className={`pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 w-max max-w-xs -translate-x-1/2 rounded-lg border-2 px-3 py-2 text-sm opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 ${LEGEND_STYLES[m.grupo] || LEGEND_STYLES.estilo}`}>
          <b>{t(`tecnicas.${m.tecnica}`, { defaultValue: m.tecnica })}</b>
          {m.nota ? <span className="block font-normal text-slate-700 mt-0.5">{m.nota}</span> : null}
        </span>
      </span>
    );
    pos = m.end;
  });
  if (pos < text.length) out.push(text.slice(pos));
  return out;
}
